import React, { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { ORDER_STATUS_LABELS, statusStyle, formatOrderRef, formatCHF, cancelDeadlineMs, CANCEL_WINDOW_MS } from "@/lib/orderStatus";
import { ChevronDown, ChevronUp, ShoppingCart, RotateCcw, XCircle, Pencil, Clock, Star, Printer } from "lucide-react";
import { useCart } from "@/contexts/CartContext";
import { SkeletonCard } from "@/components/Skeleton";
import CountdownTimer from "@/components/CountdownTimer";
import BulkActionBar, { SelectCheckbox } from "@/components/BulkActionBar";
import OrderReviewModal, { ReviewDisplay } from "@/components/OrderReviewModal";
import { generateOrderPdf } from "@/lib/orderPdf";
import { toast } from "sonner";

function StatusPill({ status }) {
    const st = statusStyle(status);
    return (
        <span
            className="text-[10px] uppercase tracking-widest px-2.5 py-1 border font-medium whitespace-nowrap"
            style={{
                color: st.color,
                background: st.background || "transparent",
                borderColor: st.border,
            }}
        >
            {ORDER_STATUS_LABELS[status] || status}
        </span>
    );
}

export default function MesCommandes() {
    const [expanded, setExpanded] = useState(null);
    const [selectedIds, setSelectedIds] = useState(() => new Set());
    const [reviewOrder, setReviewOrder] = useState(null);
    const { addItem, clear: clearCart } = useCart();
    const navigate = useNavigate();
    const qc = useQueryClient();
    const { data: orders = [], isLoading } = useQuery({
        queryKey: ["my-orders"],
        queryFn: async () => (await api.get("/orders/mine")).data,
        refetchInterval: 30_000,
    });
    const { data: prices = [] } = useQuery({
        queryKey: ["prices"],
        queryFn: async () => (await api.get("/prices")).data,
    });
    const priceByCode = React.useMemo(() => {
        const map = {};
        for (const p of prices) if (p.code) map[p.code] = p;
        return map;
    }, [prices]);

    const reorder = (order) => {
        let added = 0;
        let missing = 0;
        for (const it of order.items || []) {
            const current = it.code && priceByCode[it.code];
            if (current) {
                addItem(current, Number(it.quantity) || 1);
                added += 1;
            } else {
                missing += 1;
            }
        }
        if (added > 0) {
            toast.success(
                `${added} article${added > 1 ? "s" : ""} ajouté${added > 1 ? "s" : ""} au panier${missing > 0 ? ` · ${missing} indisponible${missing > 1 ? "s" : ""}` : ""}`
            );
            setTimeout(() => navigate("/panier"), 400);
        } else {
            toast.error("Aucun article n'est plus disponible dans le catalogue.");
        }
    };

    const cancel = useMutation({
        mutationFn: (orderId) => api.post(`/orders/${orderId}/cancel`),
        onSuccess: () => {
            toast.success("Votre commande a été annulée.");
            qc.invalidateQueries({ queryKey: ["my-orders"] });
        },
        onError: (e) => toast.error(e?.response?.data?.detail || "Impossible d'annuler la commande."),
    });

    const bulkHide = useMutation({
        mutationFn: (ids) => api.post("/orders/bulk-hide", { ids }),
        onSuccess: (res) => {
            toast.success(`${res.data?.hidden ?? 0} commande(s) retirée(s) de votre historique`);
            setSelectedIds(new Set());
            qc.invalidateQueries({ queryKey: ["my-orders"] });
        },
        onError: (e) => toast.error(e?.response?.data?.detail || "Suppression impossible."),
    });

    const cancelledOrders = useMemo(() => orders.filter((o) => o.status === "annulee"), [orders]);
    const allCancelledSelected = cancelledOrders.length > 0 && cancelledOrders.every((o) => selectedIds.has(o.id));
    const toggleSelect = (id, checked) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (checked) next.add(id);
            else next.delete(id);
            return next;
        });
    };
    const toggleAllCancelled = () => {
        if (allCancelledSelected) setSelectedIds(new Set());
        else setSelectedIds(new Set(cancelledOrders.map((o) => o.id)));
    };
    const clearSelection = () => setSelectedIds(new Set());
    const onBulkHide = () => {
        const ids = Array.from(selectedIds);
        if (ids.length === 0) return;
        if (window.confirm(`Retirer ${ids.length} commande${ids.length > 1 ? "s" : ""} annulée${ids.length > 1 ? "s" : ""} de votre historique ?\n\nLes commandes restent conservées côté administration.`)) {
            bulkHide.mutate(ids);
        }
    };

    const modifyOrder = async (order) => {
        // 1. Cancel current order
        try {
            await api.post(`/orders/${order.id}/cancel`);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Impossible de modifier cette commande.");
            return;
        }
        qc.invalidateQueries({ queryKey: ["my-orders"] });

        // 2. Rebuild cart from the cancelled order (fresh basket)
        let added = 0;
        let missing = 0;
        clearCart();
        for (const it of order.items || []) {
            const current = it.code && priceByCode[it.code];
            if (current) {
                addItem(current, Number(it.quantity) || 1);
                added += 1;
            } else {
                missing += 1;
            }
        }
        toast.success(
            `Commande annulée · ${added} article${added > 1 ? "s" : ""} chargé${added > 1 ? "s" : ""} dans votre panier${
                missing > 0 ? ` · ${missing} indisponible${missing > 1 ? "s" : ""}` : ""
            }`
        );
        setTimeout(() => navigate("/panier"), 500);
    };

    return (
        <section className="max-w-5xl mx-auto px-5 md:px-10 py-12 md:py-16" data-testid="my-orders-page">
            <p className="bc-label-small">Espace Pro</p>
            <h1 className="font-serif text-[#D4BC7C] text-4xl sm:text-5xl mt-4">Mes commandes</h1>
            <div className="bc-hairline-gold my-6" />

            {isLoading && (
                <div className="space-y-4" data-testid="my-orders-loading">
                    <SkeletonCard lines={2} />
                    <SkeletonCard lines={2} />
                    <SkeletonCard lines={2} />
                </div>
            )}

            {!isLoading && orders.length === 0 && (
                <div className="bc-card text-center py-14" data-testid="my-orders-empty">
                    <ShoppingCart size={30} color="#D4BC7C" strokeWidth={1} className="mx-auto" />
                    <p className="font-serif text-[#D4BC7C] text-2xl mt-4">Aucune commande</p>
                    <p className="text-sm text-[#A0A0A0] mt-2">Vos futures commandes apparaîtront ici.</p>
                    <Link to="/tarifs" className="bc-btn mt-8 inline-flex">
                        Voir les tarifs
                    </Link>
                </div>
            )}

            {cancelledOrders.length > 0 && (
                <div className="mb-4">
                    <BulkActionBar
                        selectedCount={selectedIds.size}
                        totalCount={cancelledOrders.length}
                        allSelected={allCancelledSelected}
                        onToggleAll={toggleAllCancelled}
                        onClear={clearSelection}
                        onBulkDelete={onBulkHide}
                        disabled={bulkHide.isPending}
                        label="commande annulée"
                    />
                </div>
            )}

            <div className="space-y-4">
                {orders.map((o) => {
                    const open = expanded === o.id;
                    const remainingMs = cancelDeadlineMs(o);
                    const canCancel = remainingMs > 0;
                    const isCancelled = o.status === "annulee";
                    return (
                        <div key={o.id} className="bc-card" data-testid={`order-${o.id}`}>
                            <div
                                className="flex flex-wrap items-start justify-between gap-3 cursor-pointer"
                                onClick={() => setExpanded(open ? null : o.id)}
                            >
                                {isCancelled && (
                                    <div className="pt-1">
                                        <SelectCheckbox
                                            checked={selectedIds.has(o.id)}
                                            onChange={(v) => toggleSelect(o.id, v)}
                                            testId={`select-order-${o.id}`}
                                        />
                                    </div>
                                )}
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-3 flex-wrap">
                                        <span className="font-serif text-[#D4BC7C] text-lg">{formatOrderRef(o.id)}</span>
                                        <StatusPill status={o.status} />
                                        {canCancel && (
                                            <CountdownTimer
                                                deadline={remainingMs}
                                                testId={`countdown-${o.id}`}
                                                onExpire={() => qc.invalidateQueries({ queryKey: ["my-orders"] })}
                                            />
                                        )}
                                    </div>
                                    <p className="text-xs text-[#A0A0A0] mt-1">
                                        Passée le {new Date(o.created_at).toLocaleDateString("fr-CH", { day: "numeric", month: "long", year: "numeric" })}
                                        {o.delivery_date && (
                                            <>
                                                {" · "}Livraison souhaitée : {new Date(o.delivery_date).toLocaleDateString("fr-CH")}
                                            </>
                                        )}
                                    </p>
                                </div>
                                <div className="text-right">
                                    <p className="font-serif text-[#D4BC7C] text-xl">{formatCHF(o.total_ht)}</p>
                                    <p className="text-[10px] text-[#A0A0A0] uppercase tracking-widest mt-1 flex items-center justify-end gap-1">
                                        {o.items.length} article{o.items.length > 1 ? "s" : ""}
                                        {open ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                                    </p>
                                </div>
                            </div>

                            {open && (
                                <div className="mt-6 pt-4 border-t space-y-2" style={{ borderColor: "var(--bc-border)" }}>
                                    {canCancel && (
                                        <div className="mb-4 p-3 flex items-start gap-3 flex-wrap" style={{
                                            background: "rgba(212,188,124,0.06)",
                                            border: "1px solid rgba(212,188,124,0.3)",
                                        }} data-testid={`cancel-window-${o.id}`}>
                                            <Clock size={14} strokeWidth={2.2} color="#D4BC7C" className="mt-0.5" />
                                            <div className="flex-1 min-w-[180px]">
                                                <p className="text-sm text-[#F2ECE0]">
                                                    Vous pouvez encore <strong className="text-[#D4BC7C]">modifier</strong> ou <strong className="text-[#D4BC7C]">annuler</strong> cette commande.
                                                </p>
                                                <p className="text-[11px] text-[#A0A0A0] mt-1">
                                                    Temps restant :{" "}
                                                    <CountdownTimer
                                                        deadline={remainingMs}
                                                        testId={`countdown-detail-${o.id}`}
                                                        showIcon={false}
                                                        onExpire={() => qc.invalidateQueries({ queryKey: ["my-orders"] })}
                                                    />
                                                </p>
                                            </div>
                                        </div>
                                    )}
                                    {o.items.map((it, idx) => (
                                        <div key={idx} className="flex justify-between items-baseline text-sm gap-4">
                                            <div>
                                                <span className="text-[#F2ECE0]">{it.article}</span>
                                                <span className="text-[10px] text-[#A0A0A0] ml-2 uppercase tracking-widest">
                                                    {it.code || "—"}
                                                </span>
                                            </div>
                                            <div className="text-right whitespace-nowrap">
                                                <span className="text-[#A0A0A0]">
                                                    {it.quantity} {it.unite || "kg"} ×{" "}
                                                    {it.prix_kg_ht != null ? `CHF ${Number(it.prix_kg_ht).toFixed(2)}` : "—"}
                                                </span>
                                                <span className="text-[#D4BC7C] ml-3 font-medium">
                                                    {it.line_total != null ? formatCHF(it.line_total) : "Sur demande"}
                                                </span>
                                            </div>
                                        </div>
                                    ))}
                                    {o.notes && (
                                        <div className="mt-4 pt-4 border-t text-sm" style={{ borderColor: "var(--bc-border)" }}>
                                            <p className="bc-label-small mb-2">Notes</p>
                                            <p className="text-[#F2ECE0]/85 whitespace-pre-line">{o.notes}</p>
                                        </div>
                                    )}
                                    <div className="pt-4 flex flex-wrap justify-end gap-2">
                                        <button
                                            type="button"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                try { generateOrderPdf(o); } catch (err) { toast.error("PDF impossible : " + (err?.message || "")); }
                                            }}
                                            className="bc-btn bc-btn--sm bc-btn--ghost"
                                            data-testid={`print-${o.id}`}
                                            title="Télécharger le bon de commande PDF"
                                        >
                                            <Printer size={14} strokeWidth={2.2} /> Imprimer
                                        </button>
                                        {o.status === "livree" && !o.review && (
                                            <button
                                                type="button"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setReviewOrder(o);
                                                }}
                                                className="bc-btn bc-btn--sm"
                                                data-testid={`review-${o.id}`}
                                                title="Évaluer cette commande livrée"
                                            >
                                                <Star size={14} strokeWidth={2.2} /> Évaluer
                                            </button>
                                        )}
                                        {canCancel && (
                                            <>
                                                <button
                                                    type="button"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        modifyOrder(o);
                                                    }}
                                                    className="bc-btn bc-btn--sm"
                                                    data-testid={`modify-${o.id}`}
                                                    disabled={cancel.isPending}
                                                    title="Annuler cette commande et charger les articles dans votre panier pour la modifier"
                                                >
                                                    <Pencil size={14} strokeWidth={2.2} /> Modifier
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        if (window.confirm("Annuler définitivement cette commande ? Aucun frais ne sera facturé.")) {
                                                            cancel.mutate(o.id);
                                                        }
                                                    }}
                                                    className="bc-btn bc-btn--sm bc-btn--danger"
                                                    data-testid={`cancel-${o.id}`}
                                                    disabled={cancel.isPending}
                                                >
                                                    <XCircle size={14} strokeWidth={2.2} /> Annuler
                                                </button>
                                            </>
                                        )}
                                        <button
                                            type="button"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                reorder(o);
                                            }}
                                            className="bc-btn bc-btn--sm"
                                            data-testid={`reorder-${o.id}`}
                                            title="Ajouter les mêmes articles au panier"
                                        >
                                            <RotateCcw size={14} strokeWidth={2.2} /> Recommander
                                        </button>
                                    </div>
                                    {o.review && (
                                        <div
                                            className="mt-3 p-3 border"
                                            style={{
                                                borderColor: o.review.has_complaint ? "#fca5a5" : "var(--bc-border)",
                                                background: o.review.has_complaint ? "rgba(252,165,165,0.05)" : "rgba(212,188,124,0.03)",
                                            }}
                                            data-testid={`review-summary-${o.id}`}
                                        >
                                            <div className="flex items-center justify-between gap-3 flex-wrap">
                                                <ReviewDisplay review={o.review} />
                                                {o.review.has_complaint && o.review.complaint_status === "resolved" && (
                                                    <span className="text-[10px] uppercase tracking-widest text-[#D4BC7C]">
                                                        Réclamation résolue
                                                    </span>
                                                )}
                                            </div>
                                            {o.review.comment && (
                                                <p className="text-xs text-[#F2ECE0]/85 mt-2 whitespace-pre-line">
                                                    «&nbsp;{o.review.comment}&nbsp;»
                                                </p>
                                            )}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            <OrderReviewModal
                open={!!reviewOrder}
                order={reviewOrder}
                onClose={() => setReviewOrder(null)}
                onSubmitted={() => qc.invalidateQueries({ queryKey: ["my-orders"] })}
            />
        </section>
    );
}
